import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Video from 'twilio-video';
import { toast} from 'react-toastify';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { 
	Box,
	CircularProgress,
	Typography,
	Button
} from '@mui/material';

import Room from './Room';

import Request from '../../components/AxiosInstance';

import { useMountEffect } from '../../hooks';
import { getMediaDeviceInfo, initialVCState } from './helpers';
import { VCControlProvider } from './VCHooks';
import { DEFAULT_VIDEO_CONSTRAINTS } from './constants';

const VideoConference = () => {
	const [roomName, setRoomName] = React.useState('');
  const [room, setRoom] = React.useState(null);
	const [hasError, setHasError] = React.useState(false);

	const { state } = useLocation();
	useMountEffect(() => {
		const { procedure } = state;
		connectRoom(procedure);
	})

	const navigate = useNavigate();

	const connectRoom = async (procedure) => {
		try {
			const { hasAudioInputDevices, hasVideoInputDevices, videoInputDevices, mediaAPISupported } = await getMediaDeviceInfo();	// Get the camera/microphone info
			if (!mediaAPISupported) {
				throw new Error('Please use Chrome (version 47 or later versions) or Edge (version 12 or later versions). Internet Explorer is not supported.');	// https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices
			}
			if (!hasAudioInputDevices || !hasVideoInputDevices) {
				throw new Error(`Unable to find devices: ${!hasVideoInputDevices && 'camera'}${!hasAudioInputDevices && ' microphone'}. Please check your device connection and retry.`);
			}

			const hasSelectedVideoDevice = videoInputDevices.some(device => device.deviceId === initialVCState.activeVideoInputId);

			const roomName = procedure.sn;
			const token = await Request.post('/api/vc/join', {
				procedureId: procedure.id
			});

			setRoomName(procedure.sn);
			
			// Connect to the room
			const videoOptions = {
				name: roomName,
				video: {
					...DEFAULT_VIDEO_CONSTRAINTS,
					...(hasSelectedVideoDevice && {deviceId: { exact: initialVCState.activeVideoInputId }})
				},
				audio: {
					deviceId: { exact: initialVCState.activeAudioInputId }
				},
			};
			const room = await Video.connect(token, videoOptions);
			setRoom(room);
		} catch(err) {
			setHasError(true);
			toast.error(`Unable to open the video conference room: ${err.message}`, {
				position: toast.POSITION.TOP_CENTER,
			})
		}
	}

	const leaveRoom = () => {
		setRoom((prevRoom) => {
			if(prevRoom) {
				prevRoom.localParticipant.tracks.forEach((trackPub) => {
					trackPub.track.stop();
				})
				prevRoom.disconnect();
			}
			return null;	// Set room to null
		})
		navigate(-1);
	}

	React.useEffect(() => {
    if (room) {
      const tidyUp = (event) => {
        if (event.persisted) {
          return;
        }
        if (room) {
          leaveRoom();
        }
      };
      window.addEventListener("pagehide", tidyUp);
      window.addEventListener("beforeunload", tidyUp);
      return () => {
        window.removeEventListener("pagehide", tidyUp);
        window.removeEventListener("beforeunload", tidyUp);
      };
    }
  }, [room]);

	return (
		<VCControlProvider>
		<Box
			sx={{
				height: '100%',
				width: '100vw',
				overflow: 'hidden',
			}}
		>
			{
				room ? 
				(
					<Room
						leaveRoom={leaveRoom}
						room={room}
						roomName={roomName}
					/>
				) :
				(
					<Box sx= {{
						height: '100vh',
						width:'100vw',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						backgroundColor: 'black',
					}}>
						{
							hasError ? 
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									alignItems: 'center',
								}}
							>
								<ErrorOutlineIcon fontSize='large' color="error" sx={{ mb: 2 }}/>
								<Typography variant="subtitle1" color="white">Error occurred. Please try again later</Typography>
								<Button
									color="warning"
									onClick={leaveRoom}
									variant="outlined"
									sx={{ mt: 3 }}
								>
									BACK
								</Button>
							</Box>
								:
							<CircularProgress/>
						}
					</Box>
				)
			}
		</Box>
		</VCControlProvider>
	);
}

export default VideoConference;